/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useState } from 'react';
import type { ContentStackBannerCta, ContentStackButton, SizePerDevice } from '../../interface';
import { Grid } from '@/components/Grid';
import { CMSImage } from '../CMSImage/CMSImage';
import styles from './CMSBannerCTA.module.scss';
import { Legal } from '@/components/AZCustomComponent/Legal';
import { mapContentstackLegalValues } from '@/utils/mapContentstackLegalValues';
import SignInModalV2 from '@/components/SignInModalV2';
import { Button } from '@az/starc-ui';
import { useSignInFlowType } from '@/components/SignInFlowTypeContext';
import { SearchStoreModal } from '@/components/Store/StoreModal/SearchStoreModal';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';
import { useLoginConfirmation } from '@/features/account/hooks/useLoginConfirmation';

type Props = {
  content: ContentStackBannerCta;
  priority?: boolean;
};

const sizePerDevice: SizePerDevice = {
  mobile: {
    width: 576,
    height: 1432,
  },
  tablet: {
    width: 1376,
    height: 510,
  },
  desktop: {
    width: 2200,
    height: 574,
  },
};

export function CMSBannerCTA({ content, priority }: Props) {
  const { showRewardSigninPopup } = useLoginConfirmation();
  const { image, legal, button, main_image_position } = content;
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const toggleStoreModal = () => {
    setStoreModalOpen((s) => !s);
  };
  const { data: userData } = useMyProfile();

  const [open, setOpen] = useState(false);

  const { setFlowTypeFn, setIsSignInModalOpened } = useSignInFlowType();

  const handleStandaloneRewards = (id: string) => {
    if (id.includes('Join')) {
      showRewardSigninPopup('SIGN_ME_UP_FORM');
      return;
    }

    showRewardSigninPopup('LINK_REWARDS_ACC');
  };

  const handleModalOpen = (id: string) => {
    if (userData?.userType === 4) {
      return handleStandaloneRewards(id);
    }
    if (id === '##search-store-modal') {
      toggleStoreModal();
      return;
    }
    setFlowTypeFn('login');
    setIsSignInModalOpened(true);
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
    setIsSignInModalOpened(false);
  };

  return (
    <>
      <Grid
        container
        spacing={0}
        justify={main_image_position === 'Left' ? 'flex-end' : 'flex-start'}
        className={styles.container}
      >
        {image && (
          <div className={styles.image}>
            <CMSImage
              image={image}
              priority={priority}
              sizePerDevice={sizePerDevice}
              className={styles.responsiveImage}
            />
          </div>
        )}
        <Grid container spacing={0} alignItems="center" className={styles.contentContainer}>
          <Grid container spacing={0} justify="center" className={styles.buttonsContainer}>
            {/* for some reason button has ContentStackButton type instead of ContentStackButton[] */}
            {(button as unknown as ContentStackButton[]).map(({ theme, link }, i) => (
              <Grid key={i} item className={styles.buttonWrapper}>
                {link && (
                  <Button
                    variant={
                      theme
                        ? (theme.toLowerCase() as Lowercase<NonNullable<typeof theme>>)
                        : undefined
                    }
                    onClick={() => handleModalOpen(link.href)}
                  >
                    {link.title}
                  </Button>
                )}
              </Grid>
            ))}
          </Grid>
          <Grid item>{legal && <Legal content={mapContentstackLegalValues(legal)} />}</Grid>
        </Grid>
        <SignInModalV2 isOpen={open} onClose={handleModalClose} />
      </Grid>
      {storeModalOpen ? <SearchStoreModal closeStoreModal={toggleStoreModal} /> : null}
    </>
  );
}
